import * as React from "react";

import type { LineDetails, Stop, Zone } from "../types";
import type { ActiveSelection } from "../atoms/activeSelection";

import { lineMatchesId } from "../utils/lineIdentity";
import { STOP_LOCATIONS_AUTO_ZOOM } from "./Map/transitMap/constants";

type Props = {
  mapRef: React.MutableRefObject<google.maps.Map | null>;
  mapLoaded: boolean;

  activeSelection: ActiveSelection;

  // URL ids
  lineIds: string[];
  stopIds: string[];
  zoneIds: string[];
  cityZoneIds: string[];

  // Hydrated selections
  selectedLines: LineDetails[];
  selectedStops: Stop[];
  selectedZones: Zone[];
  selectedCityZones: Zone[];

  // Layout + zoom
  isDesktop: boolean;
  maxZoom?: number;
  desktopLeftPadding?: number;
  skipFit?: boolean;
};

function rootIdOf(id: string | number) {
  return String(id).split("-")[0];
}

function buildBounds(args: {
  selectedLines?: LineDetails[];
  selectedStops?: Stop[];
  selectedZones?: Zone[];
  selectedCityZones?: Zone[];
}) {
  const bounds = new google.maps.LatLngBounds();
  let hasPoints = false;

  for (const line of args.selectedLines ?? []) {
    const points = line.paths?.length
      ? ([] as google.maps.LatLngLiteral[]).concat(...line.paths)
      : (line.stops ?? []).map((stop) => ({
          lat: stop.lat,
          lng: stop.lng,
        }));

    for (const point of points) {
      if (point?.lat == null || point?.lng == null) continue;

      bounds.extend({
        lat: point.lat,
        lng: point.lng,
      });

      hasPoints = true;
    }
  }

  for (const stop of args.selectedStops ?? []) {
    if (stop?.lat == null || stop?.lng == null) continue;

    bounds.extend({
      lat: stop.lat,
      lng: stop.lng,
    });

    hasPoints = true;
  }

  for (const zone of args.selectedZones ?? []) {
    for (const point of zone.polygon ?? []) {
      if (point?.lat == null || point?.lng == null) continue;

      bounds.extend({
        lat: point.lat,
        lng: point.lng,
      });

      hasPoints = true;
    }
  }

  for (const zone of args.selectedCityZones ?? []) {
    for (const point of zone.polygon ?? []) {
      if (point?.lat == null || point?.lng == null) continue;

      bounds.extend({
        lat: point.lat,
        lng: point.lng,
      });

      hasPoints = true;
    }
  }

  return hasPoints ? bounds : null;
}

export default function InitialFitBounds({
  mapRef,
  mapLoaded,

  activeSelection,

  lineIds,
  stopIds,
  zoneIds,
  cityZoneIds,

  selectedLines,
  selectedStops,
  selectedZones,
  selectedCityZones,
  skipFit = false,
  isDesktop,
  maxZoom = 19,
  desktopLeftPadding = 460,
}: Props) {
  const didInitialFitRef = React.useRef(false);
  const lastActiveFitKeyRef = React.useRef<string | null>(null);

  const pendingIdleListenerRef =
    React.useRef<google.maps.MapsEventListener | null>(null);

  React.useEffect(() => {
    return () => {
      pendingIdleListenerRef.current?.remove();
      pendingIdleListenerRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    const map = mapRef.current;

    if (!map || !mapLoaded) return;

    if (skipFit) {
      didInitialFitRef.current = true;
      lastActiveFitKeyRef.current = null;
      return;
    }

    const clearPendingZoomLimit = () => {
      pendingIdleListenerRef.current?.remove();
      pendingIdleListenerRef.current = null;
    };

    const padding: google.maps.Padding = isDesktop
      ? {
          top: 40,
          right: 40,
          bottom: 40,
          left: desktopLeftPadding,
        }
      : {
          top: 40,
          right: 40,
          bottom: 40,
          left: 40,
        };

    const fitBounds = (bounds: google.maps.LatLngBounds, fitKey?: string) => {
      clearPendingZoomLimit();

      map.fitBounds(bounds, padding);

      pendingIdleListenerRef.current = google.maps.event.addListenerOnce(
        map,
        "idle",
        () => {
          pendingIdleListenerRef.current = null;

          const currentZoom = map.getZoom();

          if (typeof currentZoom === "number" && currentZoom > maxZoom) {
            map.setZoom(maxZoom);
          }
        }
      );

      if (fitKey) {
        lastActiveFitKeyRef.current = fitKey;
      }

      didInitialFitRef.current = true;
    };

    if (activeSelection?.kind === "stop") {
      clearPendingZoomLimit();

      const activeRootId = rootIdOf(activeSelection.id);

      const activeStop = selectedStops.find(
        (stop) => rootIdOf(stop.id) === activeRootId
      );

      if (!activeStop) return;

      const fitKey = `stop:${activeRootId}`;

      if (lastActiveFitKeyRef.current === fitKey) return;

      if (!didInitialFitRef.current) {
        map.moveCamera({
          center: {
            lat: activeStop.lat,
            lng: activeStop.lng,
          },
          zoom: Math.max(map.getZoom() ?? 0, STOP_LOCATIONS_AUTO_ZOOM),
        });
      }

      lastActiveFitKeyRef.current = fitKey;
      didInitialFitRef.current = true;
      return;
    }

    if (activeSelection?.kind === "line") {
      const activeLine = selectedLines.find((line) =>
        lineMatchesId(line, activeSelection.id)
      );

      if (!activeLine) return;

      const fitKey = [
        "line",
        String(activeSelection.id),
        ...lineIds.map(String).sort(),
      ].join(":");

      if (lastActiveFitKeyRef.current === fitKey) return;

      const bounds = buildBounds({
        selectedLines: [activeLine],
      });

      if (!bounds) return;

      fitBounds(bounds, fitKey);
      return;
    }

    lastActiveFitKeyRef.current = null;

    if (didInitialFitRef.current) return;

    const hasUrlSelections =
      lineIds.length > 0 ||
      stopIds.length > 0 ||
      zoneIds.length > 0 ||
      cityZoneIds.length > 0;

    if (!hasUrlSelections) return;

    const linesReady =
      lineIds.length === 0 || selectedLines.length >= lineIds.length;

    const requestedStopRoots = new Set(stopIds.map(rootIdOf));

    const hydratedStopRoots = new Set(
      selectedStops.map((stop) => rootIdOf(stop.id))
    );

    const stopsReady =
      requestedStopRoots.size === 0 ||
      Array.from(requestedStopRoots).every((rootId) =>
        hydratedStopRoots.has(rootId)
      );

    const zonesReady =
      zoneIds.length === 0 || selectedZones.length >= zoneIds.length;

    const cityZonesReady =
      cityZoneIds.length === 0 ||
      selectedCityZones.length >= cityZoneIds.length;

    if (!linesReady || !stopsReady || !zonesReady || !cityZonesReady) {
      return;
    }

    const bounds = buildBounds({
      selectedLines,
      selectedStops,
      selectedZones,
      selectedCityZones,
    });

    if (!bounds) return;

    fitBounds(bounds);
  }, [
    mapRef,
    mapLoaded,
    activeSelection,
    lineIds,
    stopIds,
    zoneIds,
    cityZoneIds,
    selectedLines,
    selectedStops,
    selectedZones,
    selectedCityZones,
    isDesktop,
    maxZoom,
    desktopLeftPadding,
  ]);

  return null;
}
